/**
 * PWA lifecycle helpers
 * ---------------------
 *   - registerServiceWorker(): wires up /push-sw.js as soon as the app boots
 *   - capturePromptEvent():    captures Chrome's `beforeinstallprompt` so we
 *                              can show a custom "Install app" CTA later
 *   - promptInstall():         triggers that captured prompt
 *   - getInstallState():       reports current install posture for the UI
 *
 * We intentionally do nothing offline-cache-related here: the seller portal
 * is data-heavy and stale UI is more dangerous than no UI. The SW only
 * handles push + notification clicks.
 */

let deferredPrompt = null;
let installListeners = new Set();

function _emit() {
  const s = getInstallState();
  installListeners.forEach((cb) => {
    try { cb(s); } catch (_) { /* ignore */ }
  });
}

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  // Only register in production / preview, not during raw `yarn start` localhost.
  // We use a path that exists at the public root.
  window.addEventListener('load', async () => {
    let unregisteredStale = false;
    try {
      // iter-230ag + iter-230at — Production users were seeing every
      // authenticated API call return HTTP 403 with body `[]` and
      // Chrome's "Provisional headers are shown" marker — the canonical
      // signature of a stale Service Worker intercepting fetches and
      // returning a synthetic 403. Likely culprits: a CRA-default
      // `/service-worker.js`, an old workbox bundle, or any previous SW
      // from an earlier experimental deploy. Our current SW
      // (`/push-sw.js`) has NO fetch-rewrite logic — it pass-throughs
      // to network for everything. Before registering, walk the
      // existing registrations and unregister anything that isn't
      // /push-sw.js.
      //
      // iter-230at upgrade: `r.unregister()` only takes effect on the
      // NEXT navigation — the stale SW remains the controller of the
      // current page until then. For vendors who hit a 403-storm on
      // first page load, that means they see the storm AND get stuck
      // in the apiCall 30s backoff cycle. So we now force a one-time
      // reload immediately after unregistering, gated on a
      // sessionStorage flag so we can never loop.
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) {
        const url = r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || '';
        if (!url.endsWith('/push-sw.js')) {
          try {
            await r.unregister();
            unregisteredStale = true;
            console.warn('[pwa] Unregistered stale SW:', url);
          } catch (_) { /* ignore */ }
        }
      }
    } catch (_) { /* ignore */ }

    // iter-230at — One-shot reload after unregistering a stale SW so
    // its hijacked fetch handler stops returning synthetic 403s on
    // this page-view. Guarded so we never loop:
    //   1. Only reload if we actually unregistered something this load.
    //   2. Track via sessionStorage that we've done the reload — if
    //      the flag is set, skip (covers the edge case where Chrome
    //      keeps a stale registration in its cache even after our
    //      unregister succeeded).
    if (unregisteredStale && !sessionStorage.getItem('aniyor.pwa.stalesw_reloaded')) {
      try {
        sessionStorage.setItem('aniyor.pwa.stalesw_reloaded', '1');
      } catch (_) { /* ignore */ }
      // Use replace() so the broken page isn't preserved in history.
      window.location.replace(window.location.href);
      return; // don't bother registering — the reload will rerun this.
    }

    navigator.serviceWorker
      .register('/push-sw.js', { scope: '/' })
      .then((reg) => {
        // Optional: nudge any waiting SW to activate
        if (reg.waiting) reg.waiting.postMessage('SKIP_WAITING');
      })
      .catch((err) => {
        console.warn('[pwa] SW registration failed:', err);
      });
  });
}

export function capturePromptEvent() {
  if (typeof window === 'undefined') return;

  // iter-229e — Chrome logs an informational console message every time
  // we call `preventDefault()` on `beforeinstallprompt`. On
  // customer-facing storefront / login pages we don't need a custom
  // install CTA, so we only preventDefault when the user is in a
  // surface that mounts an install UI (seller portal, admin, customer
  // dashboard). Storefront customers get Chrome's native mini-infobar
  // — same UX, no console noise.
  const PORTAL_PATH_PREFIXES = [
    '/vendor',
    '/admin',
    '/orders',
    '/dashboard',
    '/profile',
    '/listings',
    '/leads',
    '/lite',
  ];
  const inPortal = () => {
    try {
      return PORTAL_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p));
    } catch {
      return false;
    }
  };

  window.addEventListener('beforeinstallprompt', (e) => {
    // Always capture the event so portal pages that load later can
    // still call .prompt(); only block the native banner on portal
    // routes where our custom CTA will handle the install.
    if (inPortal()) {
      e.preventDefault();
      deferredPrompt = e;
      _emit();
    } else {
      // Leave Chrome's native install path untouched on
      // customer/storefront pages. We still cache the event in case
      // the user navigates into the portal during the same session.
      deferredPrompt = e;
      _emit();
    }
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    try { localStorage.setItem('aniyor.pwa.installed', '1'); } catch (_) { /* ignore */ }
    _emit();
  });
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: 'unavailable' };
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  _emit();
  return choice; // { outcome: 'accepted' | 'dismissed' }
}

export function getInstallState() {
  if (typeof window === 'undefined') return { installable: false, installed: false, platform: 'ssr' };

  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||  // iOS
    localStorage.getItem('aniyor.pwa.installed') === '1';

  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);

  return {
    installed: isStandalone,
    installable: !!deferredPrompt,
    isIOS,
    isAndroid,
    platform: isIOS ? 'ios' : isAndroid ? 'android' : 'desktop',
  };
}

export function onInstallStateChange(cb) {
  installListeners.add(cb);
  return () => installListeners.delete(cb);
}
